import {
  buildScopedPhasesExpression,
  isPhaseCondition,
  phaseScopedRuntimePath,
  runtimeHelperDeclarations,
  SCOPED_PHASES_VAR,
  semanticTypeRuntimePath
} from 'Automations/nodeDefinitions/conditions'
import type { ConditionalProperties } from 'Automations/nodeDefinitions/conditions'
import { getEffectiveSemanticTypes } from 'Automations/nodeDefinitions/conditions/helpers/getUpstreamConditionalProperties/getUpstreamConditionalProperties'
import type { Condition, SwitchGroup } from '../../switch.types'

export const createSwitchExpression = ({
  groups,
  conditionalProperties
}: {
  groups: SwitchGroup[]
  conditionalProperties: ConditionalProperties
}): string => {
  if (groups.length === 0) throw new Error('switch expression has no groups')

  const groupIfBlocks = groups
    .map(group => buildGroupIfBlock({ group, conditionalProperties }))
    .join(' ')

  return `(function() { ${runtimeHelperDeclarations} ${groupIfBlocks} return "UNKNOWN"; }())`
}

export const buildGroupIfBlock = ({
  group,
  conditionalProperties
}: {
  group: SwitchGroup
  conditionalProperties: ConditionalProperties
}): string => {
  if (group.conditions.length === 0) {
    throw new Error(`group "${group.id}" has no conditions`)
  }

  const phaseConditions = group.conditions.filter(isPhaseCondition)
  if (phaseConditions.length > 0) {
    const scopedPhasesExpression = buildScopedPhasesExpression(phaseConditions)
    const scopedConditions = group.conditions.filter(
      condition => !isPhaseCondition(condition)
    )
    const scopedConditionClause = buildConditionClause({
      conditions: scopedConditions,
      conditionalProperties,
      isPhaseScoped: true
    })
    const conditionClause = [
      `${SCOPED_PHASES_VAR}.length > 0`,
      scopedConditionClause
    ]
      .filter(Boolean)
      .join(' && ')

    return `if ((function () { var ${SCOPED_PHASES_VAR} = ${scopedPhasesExpression}; return ${conditionClause}; }())) { return "${group.id}"; }`
  }

  const conditionClause = buildConditionClause({
    conditions: group.conditions,
    conditionalProperties: conditionalProperties
  })

  return `if (${conditionClause}) { return "${group.id}"; }`
}

export const buildConditionClause = ({
  conditions,
  conditionalProperties,
  isPhaseScoped = false
}: {
  conditions: Condition[]
  conditionalProperties: ConditionalProperties
  isPhaseScoped?: boolean
}): string => {
  return conditions
    .map(condition =>
      resolveConditionString({
        condition,
        conditionalProperties,
        isPhaseScoped
      })
    )
    .join(' && ')
}

export const resolveConditionString = ({
  condition,
  conditionalProperties,
  isPhaseScoped = false
}: {
  condition: Condition
  conditionalProperties: ConditionalProperties
  isPhaseScoped?: boolean
}): string => {
  const property = conditionalProperties[condition.property]
  if (!property) throw new Error(`property "${condition.property}" not found`)

  const operator = property.operators[condition.operator]
  if (!operator) {
    throw new Error(
      `operator "${condition.operator}" on property "${condition.property}" not found`
    )
  }

  // The function string's `property` arg is the runtime path of the
  // operator's effective primary semantic type — the operator's own
  // `valueSemanticType` when set, otherwise the property's. The first
  // entry is used when an array is declared. Operators that need to
  // reference a different upstream value (e.g. a sibling currency)
  // declare it via factory config rather than reading `property`.
  const effective = getEffectiveSemanticTypes(property, operator)
  // `.at(0)` rather than `[0]` so the type is `T | undefined` — the
  // empty-array case is real (operators that declare no semantic
  // requirement) and would otherwise need a non-null assertion.
  const primarySemanticType = effective.at(0)
  if (!primarySemanticType) {
    throw new Error(
      `operator "${condition.operator}" on property "${condition.property}" has no valueSemanticType`
    )
  }

  const propertyRuntimePath = isPhaseScoped
    ? phaseScopedRuntimePath({
        propertyKey: condition.property,
        semanticType: primarySemanticType
      })
    : semanticTypeRuntimePath(primarySemanticType)

  return operator.functionString({
    property: propertyRuntimePath,
    value: condition.value
  })
}
